import { HistoryResponse, ChatStreamRequest, ChatStreamRequestV2 } from "../types";
import useAuthStore from "../store/authStore"; // Added import for authStore
import { fetchAndProcessSSE } from "../utils/sseUtils"; // Import the new utility
import { logDebug, logError, logWarn } from "../utils/logging"; // Import logging utilities
import { authenticatedFetch } from '../utils/apiUtils'; // Import the new utility

// const API_BASE_URL = '/api/v1'; // Using Vite proxy - REMOVED This was causing issues in WP context

// Helper utilities to build robust API base URLs without double-appending versions
const getBackendRoot = (): string => {
  const state = useAuthStore.getState();
  const provided = state.apiUrls?.backendBaseUrl 
    || (window as unknown as { smartieAiData?: { backendApiBaseUrl?: string } })?.smartieAiData?.backendApiBaseUrl 
    || "http://127.0.0.1:8000";

  // If provided already contains /api/v1 or /api/v2, strip that suffix to get the root
  const apiMatch = provided.match(/^(.*)\/api\/(v1|v2)\/?$/);
  if (apiMatch) {
    return apiMatch[1];
  }
  // Also handle accidental double versions like .../api/v1/api/v1
  const doubleMatch = provided.match(/^(.*)\/api\/v[12](?:\/api\/v[12])\/?$/);
  if (doubleMatch) {
    return doubleMatch[1];
  }
  return provided.replace(/\/$/, "");
};

const getApiBaseUrl = (version: 'v1' | 'v2'): string => {
  const root = getBackendRoot();
  return `${root}/api/${version}`;
};

/**
 * Fetches chat history for a given session ID.
 * @param sessionId The UUID of the chat session.
 * @param limit The maximum number of messages per page.
 * @param offset The number of messages to skip.
 * @returns A promise that resolves to the HistoryResponse object.
 */
export async function fetchChatHistory(
  sessionId: string,
  limit: number = 30,
  offset: number = 0,
): Promise<HistoryResponse> {
  const API_BASE_URL = getApiBaseUrl('v1'); // Ensure single v1 base path
  const url = `${API_BASE_URL}/history?sessionId=${encodeURIComponent(
    sessionId,
  )}&limit=${limit}&offset=${offset}`;

  return authenticatedFetch<HistoryResponse>(url, 'chatService', 'fetchChatHistory');
}

/**
 * Define the new response type for session summaries (mirroring backend schema)
 * This might be better placed in a dedicated types file (e.g., types/chat.ts or types/index.ts)
 * For now, adding it here for context. If types/index.ts is preferred, we'll move it.
 */
export interface ChatSessionSummary {
  session_id: string; // UUID string
  title: string | null;
  last_activity_at: string; // ISO datetime string
  message_count: number;
}

export interface PaginatedChatSessionSummaryResponse {
  sessions: ChatSessionSummary[];
  total_sessions: number;
  limit: number;
  offset: number;
}

/**
 * Fetches paginated chat session summaries for the authenticated user.
 * @param limit The maximum number of session summaries per page.
 * @param offset The number of session summaries to skip.
 * @returns A promise that resolves to the PaginatedChatSessionSummaryResponse object.
 */
export async function fetchChatSessionSummaries(
  limit: number = 10,
  offset: number = 0,
): Promise<PaginatedChatSessionSummaryResponse> {
  const API_BASE_URL = getApiBaseUrl('v1'); // Ensure single v1 base path
  const url = `${API_BASE_URL}/history/sessions?limit=${limit}&offset=${offset}`;

  return authenticatedFetch<PaginatedChatSessionSummaryResponse>(url, 'chatService', 'fetchChatSessionSummaries');
}

/**
 * Initiates a streaming chat response from the backend and processes SSE events.
 * @param requestData The data including sessionId, message, and optional currentPageUrl.
 * @param onToken Callback for when a 'token' event is received.
 * @param onEnd Callback for when the stream ends.
 * @param onError Callback for when an error occurs (from stream or initial request).
 * @param onStreamControllerCreated Callback to provide the AbortController to the store for cancellation.
 */
export async function streamChatResponse(
  requestData: ChatStreamRequest,
  onToken: (chunk: string) => void,
  onEnd: () => void,
  onError: (errorMessage: string | Error) => void,
  onStreamControllerCreated: (controller: AbortController | null) => void,
): Promise<void> {
  const API_BASE_URL = getApiBaseUrl('v1'); // Ensure single v1 base path
  const url = `${API_BASE_URL}/chat/stream`;
  const abortController = new AbortController();
  onStreamControllerCreated(abortController); // Pass the controller to the caller

  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

  try {
    reader = await fetchAndProcessSSE({
      url,
      method: "POST",
      body: requestData,
      signal: abortController.signal,
      onOpen: () => {
        // Optional: can be logged or used to update UI state if needed
        logDebug("SSE stream opened via chatService.");
      },
      onMessage: (data, eventType) => {
        // The fetchAndProcessSSE utility sends both data and eventType.
        // For chat, we primarily care about the 'token' data or unnamed data events.
        if (eventType === "token" || !eventType) {
          // Handle 'token' or unnamed data events
          try {
            // UMW-Debug: Log raw data received by onMessage in chatService
            logDebug(
              `[chatService] onMessage (token event): Received raw data: "${data}"`,
            );
            const parsedData = JSON.parse(data);
            if (parsedData && typeof parsedData.token === "string") {
              // UMW-Debug: Log the successfully parsed token
              logDebug(
                `[chatService] onMessage (token event): Parsed token: "${parsedData.token}"`,
              );
              onToken(parsedData.token);
            } else {
              logWarn(
                "[chatService] onMessage (token event): Parsed data is missing a valid token string. Data:",
                parsedData,
              );
              // Optionally, call onError or handle as a non-token event if appropriate
            }
          } catch (e) {
            logError(
              "[chatService] onMessage (token event): Failed to parse JSON data or invalid structure. Error:",
              e,
              "Raw data:",
              data,
            );
            // Potentially call onError if this is considered a stream-breaking error
            // onError(new Error(`Failed to parse token event data: ${data.substring(0,100)}...`));
          }
        } else if (eventType === "end") {
          // fetchAndProcessSSE calls its onEnd for this, which then calls our onEnd
          // logDebug("[chatService] onMessage: Received 'end' event type.");
        } else if (eventType === "error") {
          // fetchAndProcessSSE calls its onError for this, which then calls our onError
        }
      },
      onEnd: () => {
        onEnd();
        onStreamControllerCreated(null); // Clear controller when stream ends naturally
      },
      onError: (err) => {
        // The error from fetchAndProcessSSE can be Error object or string.
        // Ensure it's propagated correctly.
        onError(err instanceof Error ? err : new Error(String(err)));
        onStreamControllerCreated(null); // Clear controller on error
      },
    });
  } catch (error) {
    // This catch block is for errors thrown directly by fetchAndProcessSSE before it even starts processing,
    // or if fetchAndProcessSSE itself is not structured to call its own onError for all its failure modes.
    // However, fetchAndProcessSSE is designed to call its onError.
    logError("Error in streamChatResponse calling fetchAndProcessSSE:", error);
    const errToReport =
      error instanceof Error ? error : new Error(String(error));
    onError(errToReport);
    onStreamControllerCreated(null); // Clear controller on error
    if (abortController.signal.aborted === false) {
      abortController.abort(); // Ensure abort if error happened outside of signal being aborted
    }
  } finally {
    // If fetchAndProcessSSE returned a reader and it wasn't cleaned up by an abort signal causing fetchAndProcessSSE to error early,
    // or if an error occurred after reader was assigned but before full cleanup in fetchAndProcessSSE's own error path (which it should have).
    // The main idea is that fetchAndProcessSSE should handle its reader if it successfully gets one.
    // This finally block is a safeguard.
    if (reader && !abortController.signal.aborted) {
      try {
        await reader.cancel("streamChatResponse cleanup");
        reader.releaseLock();
        logDebug(
          "streamChatResponse: Reader cancelled and released in finally block.",
        );
      } catch (e) {
        logError(
          "streamChatResponse: Error in finally block cleaning up reader:",
          e,
        );
      }
    }
    // Ensure the controller is cleared if it hasn't been already by onEnd/onError
    // This might be redundant if onEnd/onError always fire and clear it.
    // onStreamControllerCreated(null);
  }
}

/**
 * Initiates a streaming chat response against the v2 context-aware endpoint.
 * @param mindspaceId The ID of the mindspace associated with the chat.
 * @param requestData The request payload conforming to ChatStreamRequestV2.
 * @param onToken Callback when a token chunk is received.
 * @param onEnd Callback when the stream ends naturally.
 * @param onError Callback when an error occurs.
 * @param onStreamControllerCreated Callback that provides the AbortController for external cancellation.
 */
export async function streamChatResponseV2(
  mindspaceId: string,
  requestData: ChatStreamRequestV2,
  onToken: (chunk: string) => void,
  onEnd: () => void,
  onError: (errorMessage: string | Error) => void,
  onStreamControllerCreated: (controller: AbortController | null) => void,
): Promise<void> {
  const API_BASE_URL = getApiBaseUrl('v2'); // Ensure v2 base path
  const url = `${API_BASE_URL}/mindspaces/${mindspaceId}/chat/stream`;
  const abortController = new AbortController();
  onStreamControllerCreated(abortController);

  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

  try {
    reader = await fetchAndProcessSSE({
      url,
      method: "POST",
      body: requestData,
      signal: abortController.signal,
      onOpen: () => logDebug("SSE stream opened via chatService v2."),
      onMessage: (data, eventType) => {
        if (eventType === "token" || !eventType) {
          try {
            const parsed = JSON.parse(data);
            if (parsed && typeof parsed.token === "string") {
              onToken(parsed.token);
            }
          } catch (e) {
            logError("[chatService] v2 onMessage parse error", e, data);
          }
        }
      },
      onEnd: () => {
        onEnd();
        onStreamControllerCreated(null);
      },
      onError: (err) => {
        onError(err instanceof Error ? err : new Error(String(err)));
        onStreamControllerCreated(null);
      },
    });
  } catch (err) {
    logError("Error in streamChatResponseV2", err);
    onError(err instanceof Error ? err : new Error(String(err)));
    onStreamControllerCreated(null);
    if (!abortController.signal.aborted) {
      abortController.abort();
    }
  } finally {
    if (reader && !abortController.signal.aborted) {
      try {
        await reader.cancel("streamChatResponseV2 cleanup");
        reader.releaseLock();
      } catch (e) {
        logError("streamChatResponseV2 cleanup error", e);
      }
    }
  }
}

/**
 * Initiates a streaming chat response against the unified guest-friendly v2 endpoint.
 * This endpoint works for both authenticated and guest users. When `mindspace_id` is omitted the
 * backend treats the request as guest-only and relies on the provided `session_context` instead of
 * persistent mindspace context.
 *
 * @param requestData  The request payload conforming to ChatStreamUnifiedRequest (see types.ts).
 * @param onToken      Callback when a token chunk is received.
 * @param onEnd        Callback when the stream ends naturally.
 * @param onError      Callback when an error occurs.
 * @param onStreamControllerCreated  Callback that provides the AbortController for external cancellation.
 */
export async function streamChatResponseUnified(
  requestData: import("../types").ChatStreamUnifiedRequest,
  onToken: (chunk: string) => void,
  onEnd: () => void,
  onError: (errorMessage: string | Error) => void,
  onStreamControllerCreated: (controller: AbortController | null) => void,
): Promise<void> {
  // Build base URL pointing to /api/v2
  const API_BASE_URL = getApiBaseUrl('v2');
  const url = `${API_BASE_URL}/chat/stream`;

  const abortController = new AbortController();
  onStreamControllerCreated(abortController);

  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

  try {
    reader = await fetchAndProcessSSE({
      url,
      method: "POST",
      body: requestData,
      signal: abortController.signal,
      onOpen: () => logDebug("SSE stream opened via unified chatService."),
      onMessage: (data, eventType) => {
        if (eventType === "token" || !eventType) {
          try {
            const parsed = JSON.parse(data);
            if (parsed && typeof parsed.token === "string") {
              onToken(parsed.token);
            }
          } catch (e) {
            logError("[chatService] unified onMessage parse error", e, data);
          }
        }
      },
      onEnd: () => {
        onEnd();
        onStreamControllerCreated(null);
      },
      onError: (err) => {
        onError(err instanceof Error ? err : new Error(String(err)));
        onStreamControllerCreated(null);
      },
    });
  } catch (err) {
    logError("Error in streamChatResponseUnified", err);
    onError(err instanceof Error ? err : new Error(String(err)));
    onStreamControllerCreated(null);
    if (!abortController.signal.aborted) {
      abortController.abort();
    }
  } finally {
    if (reader && !abortController.signal.aborted) {
      try {
        await reader.cancel("streamChatResponseUnified cleanup");
        reader.releaseLock();
      } catch (e) {
        logError("streamChatResponseUnified cleanup error", e);
      }
    }
  }
}
